import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/query/react'
import { ApiResponseSerializer } from '@v2/commons/interface/ApiResponseSerializer'
import { IEligibility } from '@v2/domains/eligibilities/models/eligibilities.model'
import { type PersonsModel } from '@v2/domains/person/types/person.types'
import {
  type PersonModel,
  type PersonsFiltersModel,
  type SinglePerson,
} from '@v2/domains/person/types/person.types'

import { rootApi } from 'src/v2/store/api/rootApi'

import { type ITag } from '../../tag/tag.types'

export const personApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    persons: builder.query<PersonsModel, { params: PersonsFiltersModel }>({
      query: (queryArgs) => ({
        url: '/v7/persons',
        method: 'GET',
        params: queryArgs.params,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        const { offset, ...rest } = queryArgs.params
        return defaultSerializeQueryArgs({
          endpointName,
          endpointDefinition,
          queryArgs: rest,
        })
      },
      merge: (currentCache, newItems) => {
        return {
          ...newItems,
          records: [...(currentCache?.records || []), ...newItems.records],
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          !!currentArg?.params.offset &&
          currentArg?.params.offset !== previousArg?.params.offset
        )
      },
    }),
    personTag: builder.query<ApiResponseSerializer<ITag>, { orgId: string }>({
      query: () => ({
        url: '/persons/tags/organization',
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        return defaultSerializeQueryArgs({
          endpointName,
          endpointDefinition,
          queryArgs,
        })
      },
    }),
    personById: builder.query<PersonModel, string>({
      query: (id) => ({
        url: `/v9/persons/${id}`,
        method: 'GET',
      }),
    }),
    person: builder.query<
      SinglePerson,
      { personId: string; organizationId: string }
    >({
      queryFn: async (
        { personId, organizationId },
        _api,
        _extraOptions,
        baseQuery,
      ) => {
        try {
          const [person, personTagsBlackCarFund, eligibilities, tags] =
            await Promise.all([
              baseQuery({
                url: `/v8/persons/${personId}`,
                method: 'GET',
              }) as Promise<{ data: PersonModel }>,
              baseQuery({
                url: `/persons/${personId}/tags-black-car-fund`,
                method: 'GET',
              }) as Promise<{ data: ITag[] | [] }>,
              baseQuery({
                url: `/eligibilities/person/${personId}?organizationId=${organizationId}`,
                method: 'GET',
              }) as Promise<{
                data: ApiResponseSerializer<IEligibility>
              }>,
              baseQuery({
                url: `/persons/${personId}/${organizationId}/tags`,
                method: 'GET',
              }) as Promise<{
                data: ApiResponseSerializer<ITag>
              }>,
            ])
          const personWithBlackCarFund = {
            ...person.data,
            blackCarFund: personTagsBlackCarFund.data,
            eligibilities: eligibilities.data.records,
            tags: tags.data.records,
          }
          return { data: personWithBlackCarFund }
        } catch (error) {
          return { error } as { error: Error }
        }
      },
    }),
    personTagEdit: builder.mutation<
      ApiResponseSerializer<ITag>,
      {
        personId: string
        organizationId: string
        data: ITag[]
      }
    >({
      query: ({ organizationId, personId, data }) => {
        return {
          url: `/persons/${personId}/${organizationId}/tags`,
          method: 'PUT',
          data: { records: data },
        }
      },
    }),
  }),
})

export const {
  usePersonsQuery,
  usePersonQuery,
  usePersonTagQuery,
  usePersonTagEditMutation,
  usePersonByIdQuery,
} = personApi
